import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  HotelsAvailabilitySelectors,
  type LinkToHotelProps,
} from "@b2bportal/core-hotels";
import { getFunnelId, useDeviceType } from "@b2bportal/core-utilities";
import { useLodgingLink } from "@tenant/hotels";
import { availabilityTokenCache, funnelIdCache } from "~core-hotels/utilities";

export const useLinkToHotel = ({
  hotel,
  openSamePage = false,
  target: propTarget,
  onTokenStore,
}: LinkToHotelProps) => {
  const target = useLodgingLink(hotel.lodging.id, hotel.lodging.name);

  const search = useSelector(HotelsAvailabilitySelectors.getSearch);
  const fromDate = search?.dateRange?.from;
  const untilDate = search?.dateRange?.until;
  const guests = React.useMemo(
    () => search?.guests ?? { adults: 2, children: [] },
    [search?.guests]
  );
  const rooms = React.useMemo(() => search?.rooms ?? 1, [search?.rooms]);
  const shopToken = hotel.price?.opaqueShopRequest;

  const storeHotelInCache = React.useCallback(() => {
    if (fromDate == null || untilDate == null) {
      return;
    }
    const key = {
      lodgingId: hotel.lodging.id,
      fromDate,
      untilDate,
      guests,
      rooms,
    };

    // On desktop the shop opens in a new tab, whose sessionStorage starts
    // empty. Hand it the funnel of this search so the hotel stays attributed
    // to the search it was opened from. Attribution never blocks opening the
    // hotel, so a storage failure is swallowed.
    try {
      const funnelId = getFunnelId("lodging");
      if (funnelId != null) {
        funnelIdCache.set(key, funnelId);
      }
    } catch {
      // localStorage unavailable or full; the shop mints its own funnel.
    }

    if (shopToken == null) {
      return;
    }
    availabilityTokenCache.set(key, shopToken);
    onTokenStore?.();
  }, [
    fromDate,
    untilDate,
    shopToken,
    hotel.lodging.id,
    guests,
    rooms,
    onTokenStore,
  ]);

  const { isDesktopAndUp } = useDeviceType();
  const linkProps = useMemo(() => {
    return isDesktopAndUp && !openSamePage
      ? { target: "_blank", rel: "noopener noreferrer" }
      : {};
  }, [isDesktopAndUp, openSamePage]);

  return React.useMemo(
    () => ({
      context: {
        target: propTarget ?? target,
        linkProps,
      },
      handlers: {
        storeHotelToken: storeHotelInCache,
      },
    }),
    [linkProps, propTarget, storeHotelInCache, target]
  );
};
